import { useState, ChangeEvent, useEffect } from 'react'
import { Tooltip, Table, Spin, Button, DatePicker } from 'antd'
import '@styles'
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/es/table'

import { useGetBatchees } from '@hooks'
import { useFilters, useGeneral } from '@contexts'
import { DataApi } from '@api'

import {
  ContentContainer,
  StyledRow,
  ActionsCellContainer,
  StyledInput,
} from '../../../components/StyledComponents'
import sortBySetter from '../../../utils/sort-by-setter'

interface BatchItem {
  carrier: { id: number; businessName: string }
  closeDate: string | null
  createAt: string
  hes: string
  hesDate: string
  id: number
  openDate: string
  status: string
  ticketCount: number
  updateAt: string
}

const TplDashboardBatches = () => {
  const { filters, setFilters } = useFilters()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { billingBatch, isLoading } = useGetBatchees()
  const [hesFilter, setHesFilter] = useState<string>()
  const [loteFilter, setLoteFilter] = useState<string>()
  const [filteredData, setFilteredData] = useState<BatchItem[]>(billingBatch as BatchItem[])
  const { t } = useTranslation()

  useEffect(() => {
    setFilteredData(billingBatch as BatchItem[])
  }, [billingBatch])

  const handleDownloadExcel = (id: number, businessName: string) => {
    DataApi.getBillingBatchExcel(id).then(res => {
      const link = document.createElement('a')
      const url = window.URL.createObjectURL(res)
      link.href = url
      document.body.appendChild(link)
      link.setAttribute('download', `Lote-${id}-${businessName}.xlsx`)
      link.click()
      link.parentNode?.removeChild(link)
    })
  }

  const handleHesFilterChangeHes = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setHesFilter(value)
    const data = billingBatch.filter(
      item => value.trim() === '' || (item.hes && item.hes.includes(value.trim())),
    )

    setFilteredData(data as BatchItem[])
  }

  const handleHesFilterChangeLote = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setLoteFilter(value)
    const data = billingBatch.filter(
      item => value.trim() === '' || item.id.toString().includes(value.trim()),
    )

    setFilteredData(data as BatchItem[])
  }

  const handleHesFilterChangeDate = (date: Dayjs | null, dateString: string | string[]) => {
    const data = billingBatch.filter(
      item =>
        (typeof dateString === 'string'
          ? (dateString as string)
          : (dateString[0] as string)
        ).trim() === '' ||
        (item.createAt &&
          item.createAt.includes(
            typeof dateString === 'string'
              ? (dateString as string)
              : (dateString[0] as string).trim(),
          )),
    )

    setFilteredData(data as BatchItem[])
  }

  const columnsBatch: ColumnsType = [
    {
      title: t('tpl_dashboard_batches.id_lote'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('tpl_dashboard_batches.#_lote'),
      dataIndex: 'carrierSeq',
      key: 'carrierSeq',
      sorter: sortBySetter('carrierSeq'),
      sortDirections: ['ascend'],
    },
    {
      title: t('tpl_dashboard_batches.from'),
      dataIndex: 'openDate',
      key: 'openDate',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.createAt).isAfter(dayjs(a.createAt)) ? 1 : -1),
    },
    {
      title: t('tpl_dashboard_batches.to'),
      dataIndex: 'closeDate',
      key: 'closeDate',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.closeDate).isAfter(dayjs(a.closeDate)) ? 1 : -1),
    },
    {
      title: t('tpl_dashboard_batches.trips'),
      dataIndex: 'ticketCount',
      key: 'ticketCount',
      render: (ticketCount: number) => <span>{ticketCount ?? 0}</span>,
    },
    {
      title: t('tpl_dashboard_batches.status'),
      dataIndex: 'status',
      key: 'status',
      render: (field: string) => t(`tpl_router.billing_batch_status.${field}`),
    },
    {
      title: t('tpl_dashboard_batches.HES'),
      dataIndex: 'hes',
      key: 'hes',
      render: (field: string) => (
        <>
          {field ? (
            <span title={field}>{field.length > 20 ? `${field.slice(0, 20)}...` : field}</span>
          ) : (
            <span style={{ color: 'red' }}>N/A</span>
          )}
        </>
      ),
    },
    {
      title: t('tpl_dashboard_batches.actions'),
      key: 'acciones',
      align: 'center',
      render: (record: { id: number; carrier: { id: number; businessName: string } }) => (
        <ActionsCellContainer>
          <Tooltip title={t('tpl_dashboard_batches.download_excel')} placement="left">
            <div style={{ marginLeft: '20px' }}>
              <DownloadOutlined
                className="update"
                onClick={() => {
                  handleDownloadExcel(record.id, record.carrier.businessName)
                }}
              />
            </div>
          </Tooltip>
        </ActionsCellContainer>
      ),
    },
  ]

  if (isLoading) {
    return (
      <div className="centered-spinner">
        <Spin size="default" />
      </div>
    )
  }

  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledInput
              allowClear
              placeholder={t('tpl_dashboard_batches.search_by_HES')}
              value={hesFilter}
              onChange={handleHesFilterChangeHes}
            />
            <StyledInput
              allowClear
              placeholder={t('tpl_dashboard_batches.search_by_batch')}
              value={loteFilter}
              onChange={handleHesFilterChangeLote}
            />
            <DatePicker
              format="YYYY-MM"
              picker="month"
              onChange={handleHesFilterChangeDate}
              allowClear
              placeholder={t('tpl_dashboard_batches.select_month')}
            />
          </div>
        </div>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        <div style={{ flex: 1 }} />
      </StyledRow>
      <Table
        className="stripped-table"
        columns={columnsBatch}
        dataSource={filteredData}
        rowKey="id"
        pagination={{
          pageSize: 50,
          onChange: (page, pageSize) => {
            setFilters({
              ...filters,
              limit: pageSize ?? 5,
              page,
            })
          },
        }}
      />
    </ContentContainer>
  )
}

export { TplDashboardBatches }
